import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronLeft, ChevronRight, Lock } from 'lucide-react'
import { stripMarkdown } from '../utils/aiText'

const PastBadgesPage = ({ onBack, currentUser }) => {
  const { t, i18n } = useTranslation()
  const [months, setMonths] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedYear, setSelectedYear] = useState(null)
  // expandedKey: "year-month-level" or null
  const [expandedKey, setExpandedKey] = useState(null)

  useEffect(() => {
    fetch('/api/v1/monthly_summaries', {
      headers: { 'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '' }
    })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : []
        setMonths(list)
        if (list.length > 0) setSelectedYear(list[0].year)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  const canRead = currentUser?.can_use_monthly_summary

  const toggleExpand = (key) => setExpandedKey(prev => prev === key ? null : key)

  // 年リスト（降順）と選択中の年の月一覧
  const years = [...new Set(months.map(m => m.year))].sort((a, b) => b - a)
  const yearIndex = years.indexOf(selectedYear)
  const visibleMonths = months.filter(m => m.year === selectedYear)
  const hasOlderYear = yearIndex >= 0 && yearIndex < years.length - 1
  const hasNewerYear = yearIndex > 0

  const goOlderYear = () => { setSelectedYear(years[yearIndex + 1]); setExpandedKey(null) }
  const goNewerYear = () => { setSelectedYear(years[yearIndex - 1]); setExpandedKey(null) }

  return (
    <div className="min-h-screen bg-[#111] text-white">
      <header className="sticky top-0 z-10 bg-[#111]/95 backdrop-blur border-b border-gray-800">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={onBack}
            className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-[#2c2c2e] transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="text-base font-bold">{t('badge.past_badges')}</h1>
        </div>
      </header>

      <div className="px-4 py-4 max-w-lg mx-auto">
        {loading && (
          <p className="text-center text-gray-500 mt-12">{t('badge.loading')}</p>
        )}

        {!loading && months.length === 0 && (
          <div className="text-center mt-16">
            <p className="text-gray-400 text-sm">{t('badge.no_badges')}</p>
            <p className="text-gray-600 text-xs mt-2">{t('badge.no_badges_hint')}</p>
          </div>
        )}

        {!loading && months.length > 0 && (
          <div className="flex items-center justify-center gap-4 mb-4">
            <button
              onClick={goOlderYear}
              disabled={!hasOlderYear}
              className={`p-2 rounded-lg transition-colors ${hasOlderYear ? 'text-gray-300 hover:text-white hover:bg-[#2c2c2e]' : 'text-gray-700'}`}
              aria-label={t('badge.prev_year')}
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-base font-bold text-white w-24 text-center">{t('badge.year', { year: selectedYear })}</span>
            <button
              onClick={goNewerYear}
              disabled={!hasNewerYear}
              className={`p-2 rounded-lg transition-colors ${hasNewerYear ? 'text-gray-300 hover:text-white hover:bg-[#2c2c2e]' : 'text-gray-700'}`}
              aria-label={t('badge.next_year')}
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        )}

        {!loading && visibleMonths.map(({ year, month, badges }) => {
          const monthStr = String(month).padStart(2, '0')
          const expandedBadge = badges.find(b => expandedKey === `${year}-${month}-${b.badge_level}`)

          return (
            <div key={`${year}-${month}`} className="mb-4 bg-[#1c1c1e] rounded-2xl p-4 border border-gray-800">
              <p className="text-sm font-bold text-white mb-3">{new Date(year, month - 1).toLocaleDateString(i18n.language, { year: 'numeric', month: 'long' })}</p>

              <div className="flex gap-4">
                {badges.map(({ badge_level, content }) => {
                  const key = `${year}-${month}-${badge_level}`
                  const isExpanded = expandedKey === key

                  return (
                    <div key={badge_level} className="flex flex-col items-center gap-2">
                      <img
                        src={`/badges/badge_${monthStr}_${badge_level}.png`}
                        alt={badge_level}
                        className="w-16 h-16 rounded-xl"
                        style={{ backgroundColor: '#1c1c1e' }}
                        onError={e => { e.target.style.display = 'none' }}
                      />
                      {content && canRead ? (
                        <button
                          onClick={() => toggleExpand(key)}
                          className={`text-xs font-bold transition-colors ${isExpanded ? 'text-gray-400' : 'text-orange-400 hover:text-orange-300'}`}
                        >
                          {isExpanded ? t('badge.close') : t('badge.view_summary_short')}
                        </button>
                      ) : content && !canRead ? (
                        <div className="flex items-center gap-0.5 text-gray-600 text-xs">
                          <Lock className="w-3 h-3" />
                          <span>{t('badge.summary')}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-700">{t('badge.no_summary')}</span>
                      )}
                    </div>
                  )
                })}
              </div>

              {expandedBadge?.content && (
                <div className="mt-3 text-sm text-gray-300 leading-relaxed whitespace-pre-wrap bg-[#2c2c2e] rounded-xl p-4">
                  {stripMarkdown(expandedBadge.content)}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default PastBadgesPage
